import React, { useState } from 'react';
import { Mail, X, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCaptureBanner = () => {
  const [email, setEmail] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Check if already dismissed or subscribed
  React.useEffect(() => {
    const dismissed = localStorage.getItem('email-banner-dismissed');
    const subscribed = localStorage.getItem('email-subscribed');
    if (dismissed || subscribed) {
      setIsVisible(false);
    }
    if (subscribed) {
      setIsSubscribed(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('email-subscribed', 'true');
        localStorage.setItem('subscriber-email', email);
        setIsSubscribed(true);
        setIsVisible(false);
        
        if (data.status === 'already_subscribed') {
          toast.info("You're already subscribed!");
        } else {
          toast.success("You're subscribed! Check your inbox at 6AM for daily deals.");
        }
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Unable to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('email-banner-dismissed', Date.now().toString());
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
      <div className="container mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="hidden sm:flex p-2 bg-[#F5A623]/20 rounded-lg">
              <Bell className="h-5 w-5 text-[#F5A623]" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm sm:text-base">
                Get Daily Auction Deals at 6AM
              </p>
              <p className="text-slate-400 text-xs sm:text-sm">
                New properties delivered to your inbox every morning
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9 h-10 w-full sm:w-64 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 text-sm"
                data-testid="email-capture-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="h-10 px-4 bg-[#F5A623] hover:bg-[#E59512] text-white font-semibold text-sm whitespace-nowrap"
              data-testid="email-capture-btn"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </Button>
            <button 
              type="button"
              onClick={handleDismiss}
              className="p-2 text-slate-500 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureBanner;
